import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Save, Store, Palette, MapPin, Clock, Globe,
  Truck, DollarSign, QrCode, Loader2, Megaphone, Receipt, MessageSquare,
  ChevronRight, BarChart3, Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { createContext, useContext } from "react";

type Restaurant = Tables<"restaurants">;

interface ConfigForm {
  name: string;
  description: string;
  phone: string;
  cnpj: string;
  primary_color: string;
  logo_url: string;
  cover_url: string;
  address_street: string;
  address_number: string;
  address_neighborhood: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  opening_time: string;
  closing_time: string;
  delivery_fee: string;
  delivery_fee_per_km: string;
  delivery_free_until_km: string;
  delivery_radius_km: string;
  min_order_value: string;
  is_active: boolean;
  mp_access_token: string;
  mp_public_key: string;
  operating_days: string[];
  social_instagram: string;
  social_facebook: string;
  social_tiktok: string;
  social_website: string;
  ga_measurement_id: string;
  meta_pixel_id: string;
  is_manually_closed: boolean;
  menu_font: string;
  menu_bg_color: string;
  menu_text_color: string;
  menu_accent_color: string;
  menu_layout: string;
  menu_card_style: string;
  ai_combo_enabled: boolean;
  ai_combo_discount_percent: string;
  show_rating: boolean;
  smart_menu_enabled: boolean;
  scheduled_orders_enabled: boolean;
  split_payment_enabled: boolean;
  receipt_thank_you_message: string;
  menu_header_style: string;
  menu_button_style: string;
  menu_animations_enabled: boolean;
  menu_welcome_message: string;
  menu_category_color: string;
  accepted_payment_methods: any;
  tip_enabled: boolean;
  tip_values: number[];
}

interface ConfigContextType {
  restaurant: Restaurant | null;
  form: ConfigForm;
  update: (field: string, value: any) => void;
  toast: ReturnType<typeof useToast>["toast"];
  uploadImage: (file: File, type: "logo" | "cover") => Promise<void>;
  uploadingLogo: boolean;
  uploadingCover: boolean;
  logoInputRef: React.RefObject<HTMLInputElement>;
  coverInputRef: React.RefObject<HTMLInputElement>;
}

export const ConfigContext = createContext<ConfigContextType>(null!);
export const useConfig = () => useContext(ConfigContext);

const menuGroups = [
  {
    label: "Geral",
    items: [
      { icon: Store, label: "Informações Gerais", path: "/configuracoes", description: "Nome, telefone, CNPJ" },
      { icon: Palette, label: "Aparência", path: "/configuracoes/aparencia", description: "Cores, fontes, layout" },
      { icon: MapPin, label: "Endereço", path: "/configuracoes/endereco", description: "Localização do restaurante" },
      { icon: Clock, label: "Horário", path: "/configuracoes/horario", description: "Dias e horários de funcionamento" },
    ],
  },
  {
    label: "Canais",
    items: [
      { icon: Globe, label: "Redes Sociais", path: "/configuracoes/redes-sociais", description: "Instagram, Facebook, TikTok" },
      { icon: MessageSquare, label: "WhatsApp", path: "/configuracoes/whatsapp", description: "Bot e notificações" },
      { icon: BarChart3, label: "Analytics & Pixel", path: "/configuracoes/analytics", description: "Google Analytics, Meta Pixel" },
    ],
  },
  {
    label: "Operações",
    items: [
      { icon: Truck, label: "Delivery", path: "/configuracoes/delivery", description: "Taxas, raio e regras" },
      { icon: DollarSign, label: "Pagamento", path: "/configuracoes/pagamento", description: "Métodos e integrações" },
      { icon: Receipt, label: "Faturamento", path: "/configuracoes/faturamento", description: "Dados fiscais" },
    ],
  },
  {
    label: "Extras",
    items: [
      { icon: QrCode, label: "QR Codes", path: "/configuracoes/qr-codes", description: "Cardápio e mesas" },
      { icon: Megaphone, label: "Popups & Promoções", path: "/configuracoes/popups", description: "Banners e alertas" },
    ],
  },
];

const allItems = menuGroups.flatMap((g) => g.items);

const ConfiguracoesLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null!);
  const coverInputRef = useRef<HTMLInputElement>(null!);

  const currentItem = allItems.find((i) => i.path === location.pathname);

  const [form, setForm] = useState<ConfigForm>({
    name: "",
    description: "",
    phone: "",
    cnpj: "",
    primary_color: "#f97316",
    logo_url: "",
    cover_url: "",
    address_street: "",
    address_number: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    opening_time: "",
    closing_time: "",
    delivery_fee: "",
    delivery_fee_per_km: "",
    delivery_free_until_km: "",
    delivery_radius_km: "",
    min_order_value: "",
    is_active: true,
    mp_access_token: "",
    mp_public_key: "",
    operating_days: ["seg", "ter", "qua", "qui", "sex", "sab"],
    social_instagram: "",
    social_facebook: "",
    social_tiktok: "",
    social_website: "",
    ga_measurement_id: "",
    meta_pixel_id: "",
    is_manually_closed: false,
    menu_font: "Inter",
    menu_bg_color: "#ffffff",
    menu_text_color: "#222222",
    menu_accent_color: "",
    menu_layout: "list",
    menu_card_style: "rounded",
    ai_combo_enabled: false,
    ai_combo_discount_percent: "10",
    show_rating: false,
    smart_menu_enabled: false,
    scheduled_orders_enabled: false,
    split_payment_enabled: false,
    receipt_thank_you_message: "",
    menu_header_style: "classic",
    menu_button_style: "filled",
    menu_animations_enabled: true,
    menu_welcome_message: "",
    menu_category_color: "",
    accepted_payment_methods: null,
    tip_enabled: false,
    tip_values: [2, 5, 10],
  });

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("*").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("*").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurant(rest);

      const { data: paymentCreds } = await supabase
        .rpc("get_restaurant_payment_credentials", { p_restaurant_id: rest.id })
        .maybeSingle();

      setForm({
        name: rest.name || "",
        description: rest.description || "",
        phone: rest.phone || "",
        cnpj: rest.cnpj || "",
        primary_color: rest.primary_color || "#f97316",
        logo_url: rest.logo_url || "",
        cover_url: rest.cover_url || "",
        address_street: rest.address_street || "",
        address_number: rest.address_number || "",
        address_neighborhood: rest.address_neighborhood || "",
        address_city: rest.address_city || "",
        address_state: rest.address_state || "",
        address_zip: rest.address_zip || "",
        opening_time: rest.opening_time?.slice(0, 5) || "",
        closing_time: rest.closing_time?.slice(0, 5) || "",
        delivery_fee: rest.delivery_fee?.toString() || "0",
        delivery_fee_per_km: (rest as any).delivery_fee_per_km?.toString() || "0",
        delivery_free_until_km: (rest as any).delivery_free_until_km?.toString() || "0",
        delivery_radius_km: rest.delivery_radius_km?.toString() || "5",
        min_order_value: rest.min_order_value?.toString() || "0",
        is_active: rest.is_active,
        mp_access_token: (paymentCreds as any)?.mp_access_token || "",
        mp_public_key: (paymentCreds as any)?.mp_public_key || "",
        operating_days: Array.isArray((rest as any).operating_days) ? (rest as any).operating_days : ["seg", "ter", "qua", "qui", "sex", "sab"],
        social_instagram: (rest as any).social_instagram || "",
        social_facebook: (rest as any).social_facebook || "",
        social_tiktok: (rest as any).social_tiktok || "",
        social_website: (rest as any).social_website || "",
        ga_measurement_id: (rest as any).ga_measurement_id || "",
        meta_pixel_id: (rest as any).meta_pixel_id || "",
        is_manually_closed: !!(rest as any).is_manually_closed,
        menu_font: (rest as any).menu_font || "Inter",
        menu_bg_color: (rest as any).menu_bg_color || "#ffffff",
        menu_text_color: (rest as any).menu_text_color || "#222222",
        menu_accent_color: (rest as any).menu_accent_color || "",
        menu_layout: (rest as any).menu_layout || "list",
        menu_card_style: (rest as any).menu_card_style || "rounded",
        ai_combo_enabled: !!(rest as any).ai_combo_enabled,
        ai_combo_discount_percent: (rest as any).ai_combo_discount_percent?.toString() || "10",
        show_rating: !!(rest as any).show_rating,
        smart_menu_enabled: !!(rest as any).smart_menu_enabled,
        scheduled_orders_enabled: !!(rest as any).scheduled_orders_enabled,
        split_payment_enabled: !!(rest as any).split_payment_enabled,
        receipt_thank_you_message: (rest as any).receipt_thank_you_message || "",
        menu_header_style: (rest as any).menu_header_style || "classic",
        menu_button_style: (rest as any).menu_button_style || "filled",
        menu_animations_enabled: (rest as any).menu_animations_enabled !== false,
        menu_welcome_message: (rest as any).menu_welcome_message || "",
        menu_category_color: (rest as any).menu_category_color || "",
        accepted_payment_methods: (rest as any).accepted_payment_methods || null,
        tip_enabled: !!(rest as any).tip_enabled,
        tip_values: Array.isArray((rest as any).tip_values) ? (rest as any).tip_values : [2, 5, 10],
      });
      setLoading(false);
    };
    load();
  }, [navigate]);

  const update = (field: string, value: any) =>
    setForm((p) => ({ ...p, [field]: value }));

  const uploadImage = async (file: File, type: "logo" | "cover") => {
    if (!restaurant) return;
    const setUploading = type === "logo" ? setUploadingLogo : setUploadingCover;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${restaurant.id}/${type}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("restaurant-images")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage
        .from("restaurant-images")
        .getPublicUrl(path);
      const field = type === "logo" ? "logo_url" : "cover_url";
      update(field, urlData.publicUrl);
      toast({ title: `${type === "logo" ? "Logo" : "Capa"} enviada com sucesso!` });
    } catch (err: any) {
      toast({ title: "Erro no upload", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!restaurant) return;
    setSaving(true);
    try {
      if (form.phone && form.phone !== restaurant.phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: isAvailable } = await supabase.rpc("is_phone_available", { 
            check_phone: form.phone,
            exclude_user_id: user.id
          });
          if (!isAvailable) {
            toast({ title: "Telefone já cadastrado", description: "Este número já está sendo usado por outro usuário.", variant: "destructive" });
            setSaving(false);
            return;
          }
        }
      }

      if (form.phone !== restaurant.phone) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from("profiles")
            .update({ phone: form.phone || null })
            .eq("user_id", user.id);
        }
      }

      const { error } = await supabase.from("restaurants").update({
        name: form.name,
        description: form.description || null,
        phone: form.phone || null,
        cnpj: form.cnpj || null,
        primary_color: form.primary_color || null,
        logo_url: form.logo_url || null,
        cover_url: form.cover_url || null,
        address_street: form.address_street || null,
        address_number: form.address_number || null,
        address_neighborhood: form.address_neighborhood || null,
        address_city: form.address_city || null,
        address_state: form.address_state || null,
        address_zip: form.address_zip || null,
        opening_time: form.opening_time || null,
        closing_time: form.closing_time || null,
        delivery_fee: parseFloat(form.delivery_fee) || 0,
        delivery_fee_per_km: parseFloat(form.delivery_fee_per_km) || 0,
        delivery_free_until_km: parseFloat(form.delivery_free_until_km) || 0,
        delivery_radius_km: parseFloat(form.delivery_radius_km) || 5,
        min_order_value: parseFloat(form.min_order_value) || 0,
        is_active: form.is_active,
        operating_days: form.operating_days,
        social_instagram: form.social_instagram || null,
        social_facebook: form.social_facebook || null,
        social_tiktok: form.social_tiktok || null,
        social_website: form.social_website || null,
        ga_measurement_id: form.ga_measurement_id || null,
        meta_pixel_id: form.meta_pixel_id || null,
        is_manually_closed: form.is_manually_closed,
        menu_font: form.menu_font || "Inter",
        menu_bg_color: form.menu_bg_color || "#ffffff",
        menu_text_color: form.menu_text_color || "#222222",
        menu_accent_color: form.menu_accent_color || null,
        menu_layout: form.menu_layout || "list",
        menu_card_style: form.menu_card_style || "rounded",
        ai_combo_enabled: form.ai_combo_enabled,
        ai_combo_discount_percent: parseFloat(form.ai_combo_discount_percent) || 10,
        show_rating: form.show_rating,
        smart_menu_enabled: form.smart_menu_enabled,
        scheduled_orders_enabled: form.scheduled_orders_enabled,
        split_payment_enabled: form.split_payment_enabled,
        receipt_thank_you_message: form.receipt_thank_you_message || null,
        menu_header_style: form.menu_header_style || "classic",
        menu_button_style: form.menu_button_style || "filled",
        menu_animations_enabled: form.menu_animations_enabled !== false,
        menu_welcome_message: form.menu_welcome_message || null,
        menu_category_color: form.menu_category_color || null,
        accepted_payment_methods: form.accepted_payment_methods || null,
        tip_enabled: form.tip_enabled,
        tip_values: form.tip_values?.length > 0 ? form.tip_values : [2, 5, 10],
      } as any).eq("id", restaurant.id);

      if (error) throw error;

      await supabase.rpc("upsert_restaurant_payment_credentials", {
        p_restaurant_id: restaurant.id,
        p_mp_access_token: form.mp_access_token || null,
        p_mp_public_key: form.mp_public_key || null,
      });

      toast({ title: "Configurações salvas com sucesso!" });
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={{ restaurant, form, update, toast, uploadImage, uploadingLogo, uploadingCover, logoInputRef, coverInputRef }}>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-xl hover:bg-secondary">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                {/* Restaurant mini avatar */}
                {form.logo_url ? (
                  <img src={form.logo_url} alt="" className="w-9 h-9 rounded-xl object-cover border border-border" />
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Settings2 className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="hidden sm:block">
                  <h1 className="font-display font-bold text-base leading-tight">Configurações</h1>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>{form.name || "Meu Restaurante"}</span>
                    {currentItem && (
                      <>
                        <ChevronRight className="h-3 w-3" />
                        <span className="text-foreground font-medium">{currentItem.label}</span>
                      </>
                    )}
                  </div>
                </div>
                <h1 className="font-display font-bold text-base sm:hidden">Configurações</h1>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground gap-2 shadow-lg shadow-primary/20 h-10 px-5"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="hidden sm:inline">Salvar Alterações</span>
              <span className="sm:hidden">Salvar</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 max-w-7xl mx-auto w-full flex">
          {/* Desktop Sidebar */}
          <nav className="w-72 shrink-0 border-r border-border bg-card/50 hidden md:flex flex-col">
            <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
              {menuGroups.map((group) => (
                <div key={group.label}>
                  <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group ${
                            isActive
                              ? "bg-primary/10 text-primary shadow-sm"
                              : "text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            isActive ? "bg-primary/20" : "bg-secondary group-hover:bg-secondary"
                          }`}>
                            <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                          </div>
                          <div className="text-left flex-1 min-w-0">
                            <p className={`font-medium text-[13px] leading-tight ${isActive ? "text-primary" : ""}`}>
                              {item.label}
                            </p>
                            <p className="text-[11px] text-muted-foreground/70 truncate mt-0.5 leading-tight">
                              {item.description}
                            </p>
                          </div>
                          {isActive && (
                            <div className="w-1.5 h-6 rounded-full bg-primary shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          {/* Mobile tabs */}
          <div className="md:hidden border-b border-border bg-card/80 backdrop-blur-lg overflow-x-auto scrollbar-hide fixed top-16 left-0 right-0 z-30">
            <div className="flex gap-1 px-3 py-2">
              {allItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <main className="flex-1 overflow-auto">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 md:py-8 pt-16 md:pt-8">
              {/* Page title on content area */}
              {currentItem && (
                <motion.div
                  key={currentItem.path}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-6"
                >
                  <h2 className="font-display font-bold text-xl">{currentItem.label}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{currentItem.description}</p>
                </motion.div>
              )}
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
      </div>
    </ConfigContext.Provider>
  );
};

export default ConfiguracoesLayout;
